import type { ReleaseDate } from '../components/PackSizesPanel';
import { formatOrdinal, formatReleaseDates } from './billingDates';
import { schedulePreviews } from '../data/mockSubscriptionSchedules';
import type { SchedulePreviewId } from '../data/mockSubscriptionSchedules';

// How a subscription's pack bills: rolling from sign-up, a fixed day of the month, or set release dates
export type SubscriptionSchedule = {
  mode: 'rolling';
} | {
  mode: 'fixedDay';
  day: number;
} | {
  mode: 'releaseDates';
  dates: ReleaseDate[];
};

export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

// Missing or unknown ids are treated as Rolling
export function getScheduleById(id?: SchedulePreviewId): SubscriptionSchedule {
  return (schedulePreviews.find(preview => preview.id === id) ?? schedulePreviews[0]).schedule;
}

// Compact label for tables: "Rolling" / "Fixed day: 14th" / "Releases: 14 Jan, 14 Jul"
export function getScheduleShortLabel(schedule: SubscriptionSchedule): string {
  if (schedule.mode === 'fixedDay') return `Fixed day: ${formatOrdinal(schedule.day)}`;
  if (schedule.mode === 'releaseDates') return `Releases: ${formatReleaseDates(schedule.dates)}`;
  return 'Rolling';
}

// True when a synced member's next billing date has been moved away from their pack's dates
export function isNextPaymentOffSchedule(schedule: SubscriptionSchedule, nextPayment?: string): boolean {
  if (schedule.mode === 'rolling') return false;
  const date = parseScheduleDate(nextPayment);
  return date !== null && !isOnSchedule(schedule, date);
}

// "Fixed day: 14th of the month" / "Release dates: 14 Jan, 14 Jul"; null for rolling
export function getScheduleLabel(schedule: SubscriptionSchedule): string | null {
  if (schedule.mode === 'fixedDay') return `Fixed day: ${formatOrdinal(schedule.day)} of the month`;
  if (schedule.mode === 'releaseDates') return `Release dates: ${formatReleaseDates(schedule.dates)}`;
  return null;
}

// The next `count` dates the pack bills on, strictly after `from`
export function getScheduledDatesAfter(schedule: SubscriptionSchedule, from: Date, count: number): Date[] {
  const start = startOfDay(from);
  const result: Date[] = [];
  if (schedule.mode === 'fixedDay') {
    for (let offset = 0; result.length < count; offset++) {
      const date = new Date(start.getFullYear(), start.getMonth() + offset, schedule.day);
      if (date > start) result.push(date);
    }
    return result;
  }
  if (schedule.mode === 'releaseDates' && schedule.dates.length > 0) {
    const sorted = [...schedule.dates].sort((a, b) => a.month - b.month || a.day - b.day);
    for (let year = start.getFullYear(); result.length < count; year++) {
      for (const {
        month,
        day
      } of sorted) {
        const date = new Date(year, month - 1, day);
        if (date > start && result.length < count) result.push(date);
      }
    }
  }
  return result;
}

export function isOnSchedule(schedule: SubscriptionSchedule, date: Date): boolean {
  if (schedule.mode === 'fixedDay') return date.getDate() === schedule.day;
  if (schedule.mode === 'releaseDates') return schedule.dates.some(release => release.month === date.getMonth() + 1 && release.day === date.getDate());
  return true;
}

// Accepts "YYYY-MM-DD", "YYYY-MM-DD HH:MM" or a readable date such as "Mon, 15 Dec 2025"
export function parseScheduleDate(value?: string): Date | null {
  if (!value) return null;
  const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : startOfDay(new Date(parsed));
}

// Keeps the existing charge time when a date is moved; defaults to 9:00
export function getTimePart(value?: string): string {
  return value?.match(/(\d{2}:\d{2})$/)?.[1] ?? '09:00';
}

export function toDateKey(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export function toDateTimeValue(date: Date, time: string): string {
  return `${toDateKey(date)} ${time}`;
}

// "14 Jul"
export function formatShortDate(date: Date): string {
  return date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short'
  });
}

// "14 Jul 2027"
export function formatMediumDate(date: Date): string {
  return date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}
